import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import LightBeams from "@/components/LightBeams";
import Magnetic from "@/components/Magnetic";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero({ start = true }) {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const goContact = () => {
    const el = document.getElementById("contact");
    if (lenis && el) lenis.scrollTo(el, { offset: -20 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };
  const goWork = () => {
    const el = document.getElementById("work");
    if (lenis && el) lenis.scrollTo(el, { offset: -20 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-black"
      data-testid="hero"
    >
      {/* subtle vignette */}
      <motion.div
        style={{ y: yImg, scale: scaleImg }}
        className="absolute inset-0 z-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      </motion.div>

      {/* live moving-head light beams */}
      <LightBeams className="pointer-events-none absolute inset-0 z-[6] h-full w-full" />

      {/* spotlight */}
      <div className="pointer-events-none absolute inset-0 z-10 spotlight-top" />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-20 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-5 sm:px-8 lg:px-12 pt-28"
      >
        <motion.span
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.1, duration: 1 }}
          className="mb-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-zinc-500"
        >
          <span className="h-px w-10 bg-zinc-600" />
          Lighting & Stage Design Agency — India
        </motion.span>

        <h1 className="font-display font-extrabold uppercase leading-[0.86] tracking-tighter">
          {["We Sculpt", "With Light", "& Shadow"].map((t, i) => (
            <span key={i} className="mask-line">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate={start ? "show" : "hidden"}
                className={`block text-[15vw] sm:text-[12vw] lg:text-[9.5vw] ${
                  i === 2 ? "text-stroke" : "text-chrome"
                }`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="mt-10 max-w-xl text-base sm:text-lg font-light leading-relaxed text-zinc-400"
        >
          We are Vision Studio — a collective of designers, riggers and
          programmers who turn empty darkness into unforgettable shows. Concept
          to console.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.85, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Magnetic
            onClick={goContact}
            data-testid="hero-enquire"
            className="rounded-full bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-zinc-200"
          >
            Start a Project
          </Magnetic>
          <Magnetic
            onClick={goWork}
            data-testid="hero-work"
            className="rounded-full border border-white/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white hover:bg-white/5"
          >
            View the Work
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 text-zinc-500"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
